"use client";
import {
  EQUIPMENT_STATUS,
  EQUIPMENT_STATUS_NAME,
  EQUIPMENT_TYPE_NAME,
} from "../services/equipment-service";

export interface EquipmentRow {
  id: number;
  code: string;
  name: string;
  type: number;
  status: number;
  barcode_data?: string;
  current_order_id?: number;
  capacity?: number;
  note?: string;
  created_at: string;
}

interface EquipmentTableProps {
  rows: EquipmentRow[];
  isLoading: boolean;
  startIndex: number;
  onEdit?: (row: EquipmentRow) => void;
  onViewDetail?: (row: EquipmentRow) => void;
  onRelease?: (row: EquipmentRow) => void;
  onShowQR?: (row: EquipmentRow) => void;
  onDelete?: (row: EquipmentRow) => void;
}

const statusColor: Record<number, string> = {
  [EQUIPMENT_STATUS.AVAILABLE]: "bg-green-100 text-green-800",
  [EQUIPMENT_STATUS.IN_USE]: "bg-blue-100 text-blue-800",
  [EQUIPMENT_STATUS.MAINTENANCE]: "bg-yellow-100 text-yellow-800",
  [EQUIPMENT_STATUS.DECOMMISSIONED]: "bg-red-100 text-red-800",
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "\u2014";
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart) return "\u2014";
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "\u2014";
  return `${day}-${month}-${year}${timePart ? ` ${timePart}` : ""}`;
};

const EquipmentTable: React.FC<EquipmentTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onEdit,
  onViewDetail,
  onRelease,
  onShowQR,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm font-medium">Đang tải dữ liệu...</span>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-12 h-12 mb-3 text-gray-300">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
        </svg>
        <span className="text-sm font-medium">Không có thiết bị nào</span>
        <span className="text-xs text-gray-300 mt-1">Thêm thiết bị mới để bắt đầu</span>
      </div>
    );
  }

  return (
    <table className="min-w-full border text-sm">
      <thead className="bg-gray-100">
        <tr>
          <th className="border px-3 py-2 text-center">STT</th>
          <th className="border px-3 py-2 text-center">Mã thiết bị</th>
          <th className="border px-3 py-2 text-center">Tên thiết bị</th>
          <th className="border px-3 py-2 text-center">Loại</th>
          <th className="border px-3 py-2 text-center">Trạng thái</th>
          <th className="border px-3 py-2 text-center">Sức chứa</th>
          <th className="border px-3 py-2 text-center">Đơn hiện tại</th>
          <th className="border px-3 py-2 text-center">Thao tác</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row.id} className="hover:bg-gray-50 group">
            <td className="border px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border px-3 py-2 font-mono font-semibold">{row.code}</td>
            <td className="border px-3 py-2">{row.name || "\u2014"}</td>
            <td className="border px-3 py-2 text-center">
              {EQUIPMENT_TYPE_NAME[row.type] || "\u2014"}
            </td>
            <td className="border px-3 py-2 text-center">
              <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[row.status] || "bg-gray-100 text-gray-800"}`}>
                {EQUIPMENT_STATUS_NAME[row.status] || "\u2014"}
              </span>
            </td>
            <td className="border px-3 py-2 text-center">
              {row.capacity ? `${row.capacity} kg` : "\u2014"}
            </td>
            <td className="border px-3 py-2 text-center">
              {row.current_order_id ? (
                <span className="inline-block rounded-md bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">
                  #{row.current_order_id}
                </span>
              ) : "\u2014"}
            </td>
            <td className="border px-3 py-2">
              <div className="flex justify-center gap-2">
                {/* Xem chi tiết */}
                {onViewDetail && (
                  <button
                    type="button"
                    onClick={() => onViewDetail(row)}
                    aria-label="Xem chi tiết"
                    className="group/act relative grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm hover:bg-slate-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/act:block">
                      Xem
                    </span>
                  </button>
                )}

                {/* QR Code */}
                {onShowQR && (
                  <button
                    type="button"
                    onClick={() => onShowQR(row)}
                    aria-label="Xem QR"
                    className="group/act relative grid h-9 w-9 place-items-center rounded-full bg-indigo-500 text-white shadow-sm hover:bg-indigo-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"/>
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/act:block whitespace-nowrap">
                      QR Code
                    </span>
                  </button>
                )}

                {/* Sửa */}
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(row)}
                    aria-label="Sửa"
                    className="group/act relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm hover:bg-yellow-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547
                              9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5
                              4.5 0 011.129-1.897l9.547-9.546z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/act:block">
                      Sửa
                    </span>
                  </button>
                )}

                {/* Xóa — chỉ khi thiết bị đang trống (khả dụng, không gắn đơn) */}
                {onDelete &&
                  row.status === EQUIPMENT_STATUS.AVAILABLE &&
                  !row.current_order_id && (
                  <button
                    type="button"
                    onClick={() => onDelete(row)}
                    aria-label="Xóa thiết bị"
                    className="group/act relative grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/act:block">
                      Xóa
                    </span>
                  </button>
                )}

                {/* Trả thiết bị */}
                {row.status === EQUIPMENT_STATUS.IN_USE && onRelease && (
                  <button
                    type="button"
                    onClick={() => onRelease(row)}
                    aria-label="Trả thiết bị"
                    className="group/act relative grid h-9 w-9 place-items-center rounded-full bg-orange-500 text-white shadow-sm hover:bg-orange-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                        className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/act:block whitespace-nowrap">
                      Trả thiết bị
                    </span>
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default EquipmentTable;
